import { partition } from 'lodash';
import React from 'react';
import { AgencyProfileOption, ConsolidatedLocationView } from '@mark43/cad-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { AgencyTypeGlobal, globalAttributes } from '../../common/global-attributes';
import { useBootstrapState } from '../../bootstrap/bootstrap-provider';

type SubdivisionDepth = 1 | 2 | 3 | 4 | 5;
export const SUBDIVISION_DEPTHS: SubdivisionDepth[] = [1, 2, 3, 4, 5];

type EventClassPrefix = 'police' | 'fire' | 'ems';
type SubdivisionFieldSuffix =
    | 'Subdivision1'
    | 'Subdivision2'
    | 'Subdivision3'
    | 'Subdivision4'
    | 'Subdivision5';

type SubdivisionFieldName = `${EventClassPrefix}${SubdivisionFieldSuffix}`;
type SubdivisionOptionsPath = `${SubdivisionFieldName}Options`;
type SubdivisionLabelPath = `${SubdivisionFieldName}Label`;

export function getAgencyPrefixForAgencyType(agencyType: AgencyTypeGlobal): EventClassPrefix {
    return agencyType === globalAttributes.agencyTypeGlobal.POLICE
        ? 'police'
        : agencyType === globalAttributes.agencyTypeGlobal.FIRE
        ? 'fire'
        : 'ems';
}

/**
 * Gives keys for subdivision options for a given class. Used to access properties on the subdivision options provided by bootstrap.
 */
export function getSubdivisionOptionsKeyForAgencyTypeAndDepth(
    agencyType: AgencyTypeGlobal,
    depth: SubdivisionDepth
): SubdivisionOptionsPath {
    const agencyPrefix = getAgencyPrefixForAgencyType(agencyType);
    const fieldName: SubdivisionFieldName = `${agencyPrefix}Subdivision${depth}`;
    const optionsPath: SubdivisionOptionsPath = `${fieldName}Options`;
    return optionsPath;
}

export function getSubdivisionLabelKeyForAgencyTypeAndDepth(
    agencyType: AgencyTypeGlobal,
    depth: SubdivisionDepth
): SubdivisionLabelPath {
    const agencyPrefix = getAgencyPrefixForAgencyType(agencyType);
    const fieldName: SubdivisionFieldName = `${agencyPrefix}Subdivision${depth}`;
    const labelPath: SubdivisionLabelPath = `${fieldName}Label`;
    return labelPath;
}

type MultiAgencyClassificationDtoSubdivisionKey = `subdivision${SubdivisionDepth}AttrId`;

export function getMultiAgencyClassificationDtoSubdivisionKeyForDepth(
    depth: SubdivisionDepth
): MultiAgencyClassificationDtoSubdivisionKey {
    return `subdivision${depth}AttrId`;
}

export function useAgencyOptions(props: {
    location: ConsolidatedLocationView | null;
    baseOptions: AgencyProfileOption[];
    agencyType: AgencyTypeGlobal;
}): AgencyProfileOption[] | { label: string; options: AgencyProfileOption[] }[] {
    const { location, agencyType } = props;
    const enabledFeatures = useEnabledFeatures();
    const subdivisionOptionsByAgencyId = useBootstrapState().subdivisionOptionsBootstrapResponse
        ?.agencyIdToSubdivisionOptionsBootstrapView;
    const agencyOptions = React.useMemo(() => {
        if (enabledFeatures.multiAgencySubdivisions && location && subdivisionOptionsByAgencyId) {
            const [inJurisdictionOptions, outOfJurisdictionOptions] = partition(
                props.baseOptions,
                (agencyProfileOption) => {
                    const classificationsForAgency = location?.multiAgencyClassificationDtos.find(
                        (classificationDto) =>
                            classificationDto.agencyId === agencyProfileOption.value
                    );
                    if (!classificationsForAgency) {
                        return false;
                    }
                    for (const subdivisionDepth of SUBDIVISION_DEPTHS) {
                        const valueKeyForDepth = getMultiAgencyClassificationDtoSubdivisionKeyForDepth(
                            subdivisionDepth
                        );
                        const valueForDepth = classificationsForAgency[valueKeyForDepth];
                        const optionsForDepth =
                            subdivisionOptionsByAgencyId[agencyProfileOption.value]?.[
                                getSubdivisionOptionsKeyForAgencyTypeAndDepth(
                                    agencyType,
                                    subdivisionDepth
                                )
                            ];
                        if (valueForDepth && optionsForDepth) {
                            if (
                                optionsForDepth.some(
                                    (option) =>
                                        !option.isOutOfJurisdiction &&
                                        option.value === valueForDepth
                                )
                            ) {
                                return true;
                            }
                        }
                    }
                    return false;
                }
            );
            return [
                { label: 'In Jurisdiction', options: inJurisdictionOptions },
                { label: 'Out of Jurisdiction', options: outOfJurisdictionOptions },
            ];
        } else {
            return props.baseOptions;
        }
    }, [
        enabledFeatures.multiAgencySubdivisions,
        location,
        subdivisionOptionsByAgencyId,
        props.baseOptions,
        agencyType,
    ]);
    return agencyOptions;
}
